import { useCallback, useEffect, useState } from "react";
import {
  Bot,
  CircleDot,
  Download,
  ExternalLink,
  Eye,
  HelpCircle,
  Maximize2,
  Minimize2,
  MoreHorizontal,
  PanelRight,
  PanelsLeftRight,
  PanelsTopBottom,
  PencilLine,
  Plus,
  Printer,
  X,
  Zap,
} from "lucide-react";
import {
  useAronApiBase,
  useAronConversationInfo,
  useAronPendingWriteThreads,
} from "../../api/aronApi";
import { useAronConversationExport } from "../../hooks/useAronConversationExport";
import {
  useAronWorkbenchStore,
  type AronGroupNode,
  type AronTab,
} from "../../store/aronWorkbenchStore";
import {
  beginTabDrag,
  droppedOutsideWindow,
  hasTabDrag,
  markTabDropHandled,
  readTabDrag,
  wasTabDropHandled,
  TAB_DND_TYPE,
} from "./tabDnd";

/** Drop highlights are local state, and the event that would clear them (a drop
 * handled by a sibling, or a drag abandoned outside) never reaches this element
 * — so a stray highlight would stay painted. Clear on the end of ANY drag. */
function useClearOnDragEnd(clear: () => void) {
  useEffect(() => {
    window.addEventListener("dragend", clear);
    window.addEventListener("drop", clear);
    return () => {
      window.removeEventListener("dragend", clear);
      window.removeEventListener("drop", clear);
    };
  }, [clear]);
}

/** What ARON wants from this tab, in one glyph. */
export function AttentionMark({ kind }: { kind: "question" | "write" | "done" }) {
  if (kind === "question") {
    return (
      <HelpCircle
        size={13}
        className="shrink-0 animate-pulse text-amber-500"
        aria-label="ARON o coś pyta"
      />
    );
  }
  if (kind === "write") {
    return (
      <PencilLine
        size={13}
        className="shrink-0 animate-pulse text-red-600"
        aria-label="ARON czeka na zatwierdzenie zapisu"
      />
    );
  }
  return (
    <CircleDot size={11} className="shrink-0 text-gray-400" aria-label="nowa odpowiedź" />
  );
}

function TabItem({
  group,
  tab,
  isActive,
  onContextMenu,
  onPopOut,
}: {
  group: AronGroupNode;
  tab: AronTab;
  isActive: boolean;
  onContextMenu: (e: React.MouseEvent, tab: AronTab) => void;
  onPopOut: (convId: string) => void;
}) {
  // Lista rozmów ma tylko pierwszą stronę — wątek starszy niż 20 ostatnich
  // dociągamy pojedynczo, inaczej karta nazywa się „Rozmowa" i nie zna swojego
  // trybu ani liczby propozycji. Karta PODGLĄDU (cudza rozmowa) nie pyta o nic:
  // endpoint rozmów jest cięty do zalogowanego, więc dociąganie dałoby 404 —
  // tytuł niesie sama karta, wzięty z wiersza listy audytu.
  const readOnly = !!tab.readOnly;
  const conv = useAronConversationInfo(readOnly ? null : tab.convId);
  const { data: pendingThreads = [] } = useAronPendingWriteThreads();
  const activateTab = useAronWorkbenchStore((s) => s.activateTab);
  const closeTab = useAronWorkbenchStore((s) => s.closeTab);
  const moveTab = useAronWorkbenchStore((s) => s.moveTab);
  const attention = useAronWorkbenchStore((s) =>
    tab.convId ? s.attention[tab.convId] : undefined,
  );
  const openApproval = useAronWorkbenchStore((s) => s.openApproval);
  const setDraggingTab = useAronWorkbenchStore((s) => s.setDraggingTab);
  const apiBase = useAronApiBase();
  const [dropBefore, setDropBefore] = useState(false);
  useClearOnDragEnd(useCallback(() => setDropBefore(false), []));

  const yolo = !!conv?.autopilot;
  const thread =
    tab.convId && !readOnly
      ? pendingThreads.find((t) => t.id === tab.convId)
      : undefined;
  const title = readOnly
    ? tab.title || "Podgląd rozmowy"
    : tab.convId
      ? conv?.title || thread?.title || "Rozmowa"
      : "Nowa rozmowa";
  const pendingWrites = conv?.pending_writes_count ?? thread?.pending_writes_count ?? 0;

  const index = group.tabs.findIndex((t) => t.key === tab.key);

  return (
    <div
      draggable
      onDragStart={(e) => {
        beginTabDrag();
        setDraggingTab({ groupId: group.id, key: tab.key });
        e.dataTransfer.setData(
          TAB_DND_TYPE,
          JSON.stringify({ groupId: group.id, key: tab.key }),
        );
        // Firefox starts no drag at all without a text/plain payload.
        e.dataTransfer.setData("text/plain", title);
        e.dataTransfer.effectAllowed = "move";
      }}
      onDragEnd={(e) => {
        setDraggingTab(null);
        if (wasTabDropHandled()) return;
        // Let go past the window edge = "wyciągnij" into its own window.
        // Podgląd cudzej rozmowy zostaje na miejscu: osobne okno to czat.
        if (tab.convId && !readOnly && droppedOutsideWindow(e)) {
          onPopOut(tab.convId);
          closeTab(group.id, tab.key);
        }
      }}
      onDragOver={(e) => {
        if (!hasTabDrag(e)) return;
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        setDropBefore(true);
      }}
      onDragLeave={() => setDropBefore(false)}
      onDrop={(e) => {
        setDropBefore(false);
        setDraggingTab(null); // this drop may re-parent the tab — dragend may never fire
        const p = readTabDrag(e);
        if (!p) return;
        e.preventDefault();
        // No stopPropagation: the strip below must still see the drop so it can
        // clear its own highlight — it reads the flag and stands down.
        markTabDropHandled();
        moveTab(p.groupId, p.key, group.id, index);
      }}
      onContextMenu={(e) => onContextMenu(e, tab)}
      onMouseDown={(e) => {
        if (e.button === 1) {
          e.preventDefault();
          closeTab(group.id, tab.key);
        }
      }}
      title={
        readOnly
          ? `${title} — podgląd cudzej rozmowy, tylko do odczytu`
          : `${title}${
              attention === "question" ? " — ARON o coś pyta" : ""
            }\nPrzeciągnij, aby przełożyć kartę; na brzeg rozmowy — aby podzielić; poza okno — aby wydzielić.`
      }
      className={`group/tab relative flex max-w-[15rem] shrink-0 items-center gap-1.5 border-r border-gray-200 px-2 py-1 text-xs dark:border-gray-700 ${
        isActive
          ? `border-t-2 bg-white text-gray-900 dark:bg-gray-900 dark:text-gray-100 ${
              yolo ? "border-t-black dark:border-t-white" : "border-t-red-600"
            }`
          : "border-t-2 border-t-transparent bg-gray-100 text-gray-500 hover:bg-gray-200/70 dark:bg-gray-950 dark:hover:bg-gray-800"
      }`}
    >
      {dropBefore && <span className="absolute inset-y-0 left-0 w-0.5 bg-red-600" />}
      {/* A <button> here would swallow the drag: form controls do not hand the
          gesture to their draggable ancestor. Plain div + role="tab". */}
      <div
        role="tab"
        tabIndex={0}
        aria-selected={isActive}
        onClick={() => activateTab(group.id, tab.key)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            activateTab(group.id, tab.key);
          }
        }}
        className="flex min-w-0 flex-1 cursor-pointer select-none items-center gap-1.5 text-left"
      >
        {readOnly ? (
          <Eye
            size={13}
            className="shrink-0 text-gray-400"
            aria-label="podgląd cudzej rozmowy"
          />
        ) : conv?.is_generating ? (
          <span
            className={`inline-block h-3 w-3 shrink-0 animate-spin rounded-full border-2 border-t-transparent ${
              yolo ? "border-black dark:border-white" : "border-red-500"
            }`}
            title="ARON generuje odpowiedź…"
          />
        ) : yolo ? (
          <Zap
            size={13}
            className="shrink-0 text-black dark:text-white"
            aria-label="tryb YOLO"
          />
        ) : (
          <Bot size={13} className="shrink-0 text-red-500" />
        )}
        <span className="min-w-0 flex-1 truncate">{title}</span>
        {attention && <AttentionMark kind={attention} />}
      </div>
      {pendingWrites > 0 && tab.convId && (
        <button
          type="button"
          onClick={() => openApproval({ convId: tab.convId!, apiBase })}
          title={`${pendingWrites} propozycji zapisu czeka na Twoją decyzję — kliknij, aby zdecydować`}
          className="shrink-0 bg-red-600 px-1 font-mono text-[10px] font-semibold text-white hover:bg-red-700"
        >
          {pendingWrites}
        </button>
      )}
      <button
        type="button"
        onClick={() => closeTab(group.id, tab.key)}
        title="Zamknij kartę"
        className={`shrink-0 text-gray-400 hover:text-red-600 ${
          isActive ? "" : "opacity-0 group-hover/tab:opacity-100"
        }`}
      >
        <X size={13} />
      </button>
    </div>
  );
}

const MENU_ITEM =
  "flex w-full items-center gap-2 px-3 py-1.5 text-left text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-40 dark:text-gray-200 dark:hover:bg-gray-800";

/** A floating menu anchored at the click point (tab context menu / "…" menu). */
function TabMenu({
  at,
  onClose,
  children,
}: {
  at: { x: number; y: number };
  onClose: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    const close = () => onClose();
    window.addEventListener("click", close);
    window.addEventListener("resize", close);
    return () => {
      window.removeEventListener("click", close);
      window.removeEventListener("resize", close);
    };
  }, [onClose]);
  return (
    <div
      style={{ left: Math.min(at.x, window.innerWidth - 260), top: at.y }}
      onClick={(e) => e.stopPropagation()}
      className="fixed z-50 w-60 border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
    >
      {children}
    </div>
  );
}

interface MenuState {
  x: number;
  y: number;
  /** Set for a tab's context menu; absent for the pane's "…" menu. */
  tab?: AronTab;
}

export function AronTabBar({
  group,
  isActiveGroup,
  groupCount,
  onPopOut,
  onDetach,
}: {
  group: AronGroupNode;
  isActiveGroup: boolean;
  groupCount: number;
  /** Open this conversation in a separate browser window. */
  onPopOut: (convId: string) => void;
  /** Move this conversation into the global right-hand dock. */
  onDetach: (convId: string) => void;
}) {
  const openDraft = useAronWorkbenchStore((s) => s.openDraft);
  const splitGroup = useAronWorkbenchStore((s) => s.splitGroup);
  const closeGroup = useAronWorkbenchStore((s) => s.closeGroup);
  const closeTab = useAronWorkbenchStore((s) => s.closeTab);
  const closeOtherTabs = useAronWorkbenchStore((s) => s.closeOtherTabs);
  const detachTabToNewGroup = useAronWorkbenchStore((s) => s.detachTabToNewGroup);
  const moveTab = useAronWorkbenchStore((s) => s.moveTab);
  const focusGroup = useAronWorkbenchStore((s) => s.focusGroup);
  const maximizedGroupId = useAronWorkbenchStore((s) => s.maximizedGroupId);
  const toggleMaximize = useAronWorkbenchStore((s) => s.toggleMaximize);
  const setDraggingTab = useAronWorkbenchStore((s) => s.setDraggingTab);

  const [menu, setMenu] = useState<MenuState | null>(null);
  const [dropEnd, setDropEnd] = useState(false);
  useClearOnDragEnd(useCallback(() => setDropEnd(false), []));

  const activeTab = group.tabs.find((t) => t.key === group.activeKey);
  const menuTab = menu?.tab ?? activeTab;
  const menuConvId = menuTab?.convId ?? null;
  // Podgląd cudzej rozmowy: druk, pobranie zapisu, wydzielenie i osobne okno
  // chodzą po endpointach właściciela wątku (404 na cudzym) — dla takiej karty
  // pozostają wyszarzone, transkrypt czyta się w panelu.
  const menuReadOnly = !!menuTab?.readOnly;
  const { exporting, downloadLog, printConversation, hasMessages } =
    useAronConversationExport(menuReadOnly ? null : menuConvId);

  const maximized = maximizedGroupId === group.id;
  const btn =
    "inline-flex h-6 w-6 items-center justify-center text-gray-400 transition-colors hover:bg-gray-200 hover:text-red-600 dark:hover:bg-gray-800";

  return (
    <div
      onMouseDown={() => !isActiveGroup && focusGroup(group.id)}
      className="flex shrink-0 items-stretch border-b border-gray-200 bg-gray-100 dark:border-gray-700 dark:bg-gray-950"
    >
      <div
        className="flex min-w-0 flex-1 items-stretch overflow-x-auto"
        onDragOver={(e) => {
          if (!hasTabDrag(e)) return;
          e.preventDefault();
          setDropEnd(true);
        }}
        onDragLeave={() => setDropEnd(false)}
        onDrop={(e) => {
          setDropEnd(false);
          setDraggingTab(null);
          if (wasTabDropHandled()) return; // a tab in this strip already placed it
          const p = readTabDrag(e);
          if (!p) return;
          e.preventDefault();
          markTabDropHandled();
          moveTab(p.groupId, p.key, group.id);
        }}
      >
        {group.tabs.map((t) => (
          <TabItem
            key={t.key}
            group={group}
            tab={t}
            isActive={group.activeKey === t.key}
            onPopOut={onPopOut}
            onContextMenu={(e, tab) => {
              e.preventDefault();
              focusGroup(group.id);
              setMenu({ x: e.clientX, y: e.clientY, tab });
            }}
          />
        ))}
        <div className={`min-w-8 flex-1 ${dropEnd ? "bg-red-100 dark:bg-red-900/30" : ""}`} />
      </div>

      <div className="flex shrink-0 items-center gap-0.5 px-1">
        <button
          type="button"
          onClick={() => openDraft(group.id)}
          title="Nowa rozmowa w tym panelu"
          className={btn}
        >
          <Plus size={14} />
        </button>
        <button
          type="button"
          onClick={() => splitGroup(group.id, "row")}
          title="Podziel w prawo — ta rozmowa obok"
          className={btn}
        >
          <PanelsLeftRight size={14} />
        </button>
        <button
          type="button"
          onClick={() => splitGroup(group.id, "column")}
          title="Podziel w dół — ta rozmowa pod spodem"
          className={btn}
        >
          <PanelsTopBottom size={14} />
        </button>
        <button
          type="button"
          onClick={() => toggleMaximize(group.id)}
          title={maximized ? "Przywróć pozostałe panele" : "Rozszerz ten panel na całość"}
          className={btn}
        >
          {maximized ? <Minimize2 size={14} /> : <Maximize2 size={14} />}
        </button>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            focusGroup(group.id);
            setMenu({ x: e.clientX, y: e.clientY });
          }}
          title="Więcej: drukuj, pobierz zapis, wydziel…"
          className={btn}
        >
          <MoreHorizontal size={14} />
        </button>
        {groupCount > 1 && (
          <button
            type="button"
            onClick={() => closeGroup(group.id)}
            title="Zamknij ten panel"
            className={btn}
          >
            <X size={14} />
          </button>
        )}
      </div>

      {menu && (
        <TabMenu at={menu} onClose={() => setMenu(null)}>
          <button
            type="button"
            className={MENU_ITEM}
            disabled={!menuConvId || menuReadOnly || !hasMessages}
            onClick={() => {
              printConversation();
              setMenu(null);
            }}
          >
            <Printer size={13} /> Drukuj / zapisz PDF
          </button>
          <button
            type="button"
            className={MENU_ITEM}
            disabled={!menuConvId || menuReadOnly || exporting}
            onClick={() => {
              void downloadLog();
              setMenu(null);
            }}
          >
            <Download size={13} /> {exporting ? "Pobieram…" : "Pobierz zapis rozmowy"}
          </button>
          <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
          <button
            type="button"
            className={MENU_ITEM}
            onClick={() => {
              splitGroup(group.id, "row", menuTab?.key);
              setMenu(null);
            }}
          >
            <PanelsLeftRight size={13} /> Podziel w prawo
          </button>
          <button
            type="button"
            className={MENU_ITEM}
            onClick={() => {
              splitGroup(group.id, "column", menuTab?.key);
              setMenu(null);
            }}
          >
            <PanelsTopBottom size={13} /> Podziel w dół
          </button>
          <button
            type="button"
            className={MENU_ITEM}
            disabled={group.tabs.length < 2 || !menuTab}
            onClick={() => {
              if (menuTab) detachTabToNewGroup(group.id, menuTab.key, "row");
              setMenu(null);
            }}
          >
            <PanelsLeftRight size={13} /> Przenieś do panelu obok
          </button>
          <button
            type="button"
            className={MENU_ITEM}
            disabled={!menuConvId || menuReadOnly}
            onClick={() => {
              if (menuConvId && !menuReadOnly) onDetach(menuConvId);
              setMenu(null);
            }}
          >
            <PanelRight size={13} /> Wydziel do panelu przy stronie
          </button>
          <button
            type="button"
            className={MENU_ITEM}
            disabled={!menuConvId || menuReadOnly}
            title="Możesz też po prostu przeciągnąć kartę poza okno przeglądarki"
            onClick={() => {
              if (menuConvId && !menuReadOnly) onPopOut(menuConvId);
              setMenu(null);
            }}
          >
            <ExternalLink size={13} />
            <span className="whitespace-nowrap">Otwórz w nowym oknie</span>
          </button>
          <div className="my-1 border-t border-gray-200 dark:border-gray-700" />
          <button
            type="button"
            className={MENU_ITEM}
            disabled={!menuTab}
            onClick={() => {
              if (menuTab) closeTab(group.id, menuTab.key);
              setMenu(null);
            }}
          >
            <X size={13} /> Zamknij kartę
          </button>
          <button
            type="button"
            className={MENU_ITEM}
            disabled={group.tabs.length < 2 || !menuTab}
            onClick={() => {
              if (menuTab) closeOtherTabs(group.id, menuTab.key);
              setMenu(null);
            }}
          >
            <X size={13} /> Zamknij pozostałe karty
          </button>
        </TabMenu>
      )}
    </div>
  );
}
